// hooks/useAuth.tsx
import React, { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { AuthService } from '../services/authService';
import { User } from '../types/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loginMaster: (date: Date) => Promise<void>;
  isAuthenticated: boolean;
  isMaster: boolean;
  isGrupo: boolean;
  isEmpresa: boolean;
  canManageEmpresas: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = () => {
      try {
        const currentUser = AuthService.getCurrentUser();
        const isAuthenticated = AuthService.isAuthenticated();
        
        if (isAuthenticated && currentUser) {
          setUser(currentUser);
        } else {
          // Limpar dados inconsistentes
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
        }
      } catch (error) {
        console.error('Erro ao inicializar autenticação:', error);
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
    // Listen for global auth changes (login/logout) so we can update context
    const onAuthChange = (e: Event) => {
      try {
        // @ts-ignore - custom event
        const detail = (e as CustomEvent).detail;
        const updatedUser = detail ?? AuthService.getCurrentUser();
        setUser(updatedUser);
      } catch (err) {
        setUser(AuthService.getCurrentUser());
      }
    };

    window.addEventListener('auth:change', onAuthChange as EventListener);

    return () => {
      window.removeEventListener('auth:change', onAuthChange as EventListener);
    };
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    setLoading(true);
    try {
      const loggedUser = await AuthService.login(email, password);
      setUser(loggedUser);
    } finally {
      setLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    setLoading(true);
    try {
      await AuthService.logout();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const loginMaster = async (date: Date): Promise<void> => {
    setLoading(true);
    try {
      const loggedUser = await AuthService.loginMaster(date);
      setUser(loggedUser);
    } finally {
      setLoading(false);
    }
  };

  const contextValue: AuthContextType = {
    user,
    loading,
    login,
    logout,
    loginMaster,
    isAuthenticated: AuthService.isAuthenticated(),
    isMaster: AuthService.isMaster(),
    isGrupo: AuthService.isGrupo(),
    isEmpresa: AuthService.isEmpresa(),
    canManageEmpresas: AuthService.canManageEmpresas(),
  };

  return React.createElement(
    AuthContext.Provider,
    { value: contextValue },
    children
  );
};

// Hook mais simples para quando não precisar do Provider
export const useAuthSimple = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const currentUser = AuthService.getCurrentUser();
    setUser(currentUser);
    setLoading(false);

    const onAuthChange = (e: Event) => {
      try {
        // @ts-ignore
        const detail = (e as CustomEvent).detail;
        setUser(detail ?? AuthService.getCurrentUser());
      } catch (err) {
        setUser(AuthService.getCurrentUser());
      }
    };

    window.addEventListener('auth:change', onAuthChange as EventListener);

    return () => {
      window.removeEventListener('auth:change', onAuthChange as EventListener);
    };
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    setLoading(true);
    try {
      const user = await AuthService.login(email, password);
      setUser(user);
    } finally {
      setLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    setLoading(true);
    try {
      await AuthService.logout();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const loginMaster = async (date: Date): Promise<void> => {
    setLoading(true);
    try {
      const user = await AuthService.loginMaster(date);
      setUser(user);
    } finally {
      setLoading(false);
    }
  };

  return {
    user,
    loading,
    login,
    logout,
    loginMaster,
    isAuthenticated: AuthService.isAuthenticated(),
    isMaster: AuthService.isMaster(),
    isGrupo: AuthService.isGrupo(),
    isEmpresa: AuthService.isEmpresa(),
    canManageEmpresas: AuthService.canManageEmpresas(),
  };
};